import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import MainNavigation from '../../components/ui/MainNavigation';
import Footer from '../home-page/components/Footer';

const CookiePolicy = () => {
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <MainNavigation />
      
      <main className="flex-1 py-8 md:py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 md:mb-12">
            <Link 
              to="/" 
              className="inline-flex items-center space-x-2 text-sm text-muted-foreground hover:text-primary transition-smooth mb-4"
            >
              <Icon name="ArrowLeft" size={16} />
              <span>Back to Home</span>
            </Link>
            
            <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
              Cookie Policy
            </h1>
            
            <p className="font-body text-base text-muted-foreground">
              <strong>Effective Date:</strong> February 11, 2026
            </p>
          </div>

          {/* Introduction */}
          <div className="mb-8">
            <p className="font-body text-base text-muted-foreground leading-relaxed mb-4">
              This Cookie Policy explains how Draggin Trailers LLC ("Company," "we," "us," or "our") uses cookies and similar technologies when you visit our website or use our mobile application (collectively, the "Platform").
            </p>
            <p className="font-body text-base text-muted-foreground leading-relaxed">
              This Policy should be read together with our Privacy Policy.
            </p>
          </div>

          {/* Content Sections */}
          <div className="space-y-4">
            {/* What Are Cookies */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('what-are-cookies')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <Icon name="Cookie" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">What Are Cookies?</h2>
                </div>
                <Icon 
                  name="ChevronDown" 
                  size={24} 
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['what-are-cookies'] ? 'rotate-180' : ''}`}
                />
              </button>
              {openSections?.['what-are-cookies'] && (
                <div className="px-6 pb-6 space-y-4">
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    Cookies are small text files placed on your device (computer, smartphone, or tablet) when you visit a website. Cookies help websites function properly, improve performance, and provide analytical information.
                  </p>
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    We may also use similar technologies such as:
                  </p>
                  <ul className="space-y-2 ml-6">
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Web beacons</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Pixels</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Local storage</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>SDK tracking technologies (for mobile apps)</span>
                    </li>
                  </ul>
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    For simplicity, we refer to all of these as "cookies."
                  </p>
                </div>
              )}
            </div>

            {/* Types of Cookies */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('types-of-cookies')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <Icon name="List" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Types of Cookies We Use</h2>
                </div>
                <Icon 
                  name="ChevronDown" 
                  size={24} 
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['types-of-cookies'] ? 'rotate-180' : ''}`}
                />
              </button>
              {openSections?.['types-of-cookies'] && (
                <div className="px-6 pb-6 space-y-6">
                  {/* Strictly Necessary */}
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-3">A. Strictly Necessary Cookies</h3>
                    <p className="font-body text-base text-muted-foreground leading-relaxed mb-3">
                      These cookies are required for the Platform to function properly. They enable:
                    </p>
                    <ul className="space-y-2 ml-6">
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Account login</span>
                      </li>
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Booking checkout</span>
                      </li>
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Payment processing</span>
                      </li>
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Security authentication</span>
                      </li>
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Fraud prevention</span>
                      </li>
                    </ul>
                    <p className="font-body text-base text-muted-foreground leading-relaxed mt-3">
                      Without these cookies, certain features may not operate. These cookies cannot be disabled through our systems.
                    </p>
                  </div>

                  {/* Performance & Analytics */}
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-3">B. Performance & Analytics Cookies</h3>
                    <p className="font-body text-base text-muted-foreground leading-relaxed mb-3">
                      These cookies help us understand how users interact with the Platform, including:
                    </p>
                    <ul className="space-y-2 ml-6">
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Pages visited</span>
                      </li>
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Time spent on pages</span>
                      </li>
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Error messages</span>
                      </li>
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Traffic patterns</span>
                      </li>
                    </ul>
                    <p className="font-body text-base text-muted-foreground leading-relaxed mt-3">
                      This information is aggregated and used to improve functionality and user experience. We may use third-party analytics providers to collect this data.
                    </p>
                  </div>

                  {/* Functional */}
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-3">C. Functional Cookies</h3>
                    <p className="font-body text-base text-muted-foreground leading-relaxed mb-3">
                      These cookies remember your preferences, such as:
                    </p>
                    <ul className="space-y-2 ml-6">
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Saved login details</span>
                      </li>
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Location preferences</span>
                      </li>
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Language settings</span>
                      </li>
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Previously viewed listings</span>
                      </li>
                    </ul>
                    <p className="font-body text-base text-muted-foreground leading-relaxed mt-3">
                      These enhance convenience but are not strictly required.
                    </p>
                  </div>

                  {/* Advertising & Marketing */}
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-3">D. Advertising & Marketing Cookies</h3>
                    <p className="font-body text-base text-muted-foreground leading-relaxed mb-3">
                      These cookies may be used to:
                    </p>
                    <ul className="space-y-2 ml-6">
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Deliver relevant advertisements</span>
                      </li>
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Measure ad performance</span>
                      </li>
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Prevent repetitive advertising</span>
                      </li>
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Track marketing campaign effectiveness</span>
                      </li>
                    </ul>
                    <p className="font-body text-base text-muted-foreground leading-relaxed mt-3">
                      We may work with third-party advertising partners who use cookies or similar technologies.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Third-Party Cookies */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('third-party-cookies')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <Icon name="Users" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Third-Party Cookies</h2>
                </div>
                <Icon 
                  name="ChevronDown" 
                  size={24} 
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['third-party-cookies'] ? 'rotate-180' : ''}`}
                />
              </button>
              {openSections?.['third-party-cookies'] && (
                <div className="px-6 pb-6 space-y-4">
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    Some cookies are placed by third parties that provide services to us, including:
                  </p>
                  <ul className="space-y-2 ml-6">
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Payment processors</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Analytics providers</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Fraud detection services</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Marketing partners</span>
                    </li>
                  </ul>
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    These third parties may collect information in accordance with their own privacy policies. Draggin Trailers LLC does not control third-party cookie policies.
                  </p>
                </div>
              )}
            </div>

            {/* How We Use Cookie Information */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('cookie-usage')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <Icon name="Settings" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">How We Use Cookie Information</h2>
                </div>
                <Icon 
                  name="ChevronDown" 
                  size={24} 
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['cookie-usage'] ? 'rotate-180' : ''}`}
                />
              </button>
              {openSections?.['cookie-usage'] && (
                <div className="px-6 pb-6 space-y-4">
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    We use cookies to:
                  </p>
                  <ul className="space-y-2 ml-6">
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Maintain account sessions</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Process bookings securely</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Prevent fraud and abuse</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Improve website performance</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Analyze usage trends</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Support marketing efforts</span>
                    </li>
                  </ul>
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    Cookies help ensure safe marketplace transactions.
                  </p>
                </div>
              )}
            </div>

            {/* Your Cookie Choices */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('cookie-choices')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <Icon name="Sliders" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Your Cookie Choices</h2>
                </div>
                <Icon 
                  name="ChevronDown" 
                  size={24} 
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['cookie-choices'] ? 'rotate-180' : ''}`}
                />
              </button>
              {openSections?.['cookie-choices'] && (
                <div className="px-6 pb-6 space-y-6">
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-3">Browser Settings</h3>
                    <p className="font-body text-base text-muted-foreground leading-relaxed mb-3">
                      Most web browsers allow you to:
                    </p>
                    <ul className="space-y-2 ml-6">
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Block cookies</span>
                      </li>
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Delete stored cookies</span>
                      </li>
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Receive alerts before cookies are placed</span>
                      </li>
                    </ul>
                    <p className="font-body text-base text-muted-foreground leading-relaxed mt-3">
                      Please note that disabling cookies may impact functionality.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-3">Do Not Track Signals</h3>
                    <p className="font-body text-base text-muted-foreground leading-relaxed">
                      Some browsers transmit "Do Not Track" signals. The Platform does not currently respond differently to these signals unless required by law.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-3">State Privacy Rights</h3>
                    <p className="font-body text-base text-muted-foreground leading-relaxed mb-3">
                      Residents of certain states (including California, Colorado, Virginia, Connecticut, and Utah) may have rights to:
                    </p>
                    <ul className="space-y-2 ml-6">
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Opt out of targeted advertising</span>
                      </li>
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Request access to personal data</span>
                      </li>
                      <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                        <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                        <span>Request deletion of personal data</span>
                      </li>
                    </ul>
                    <p className="font-body text-base text-muted-foreground leading-relaxed mt-3">
                      To exercise applicable privacy rights, please refer to our Privacy Policy.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Data Retention */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('data-retention')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Data Retention</h2>
                </div>
                <Icon 
                  name="ChevronDown" 
                  size={24} 
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['data-retention'] ? 'rotate-180' : ''}`}
                />
              </button>
              {openSections?.['data-retention'] && (
                <div className="px-6 pb-6 space-y-4">
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    Cookies may remain on your device:
                  </p>
                  <ul className="space-y-2 ml-6">
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>For the duration of your browsing session (session cookies), or</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>For a longer period (persistent cookies) depending on purpose</span>
                    </li>
                  </ul>
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    You may delete cookies at any time through your browser settings.
                  </p>
                </div>
              )}
            </div>

            {/* Mobile App Tracking */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('mobile-tracking')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <Icon name="Smartphone" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Mobile App Tracking</h2>
                </div>
                <Icon 
                  name="ChevronDown" 
                  size={24} 
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['mobile-tracking'] ? 'rotate-180' : ''}`}
                />
              </button>
              {openSections?.['mobile-tracking'] && (
                <div className="px-6 pb-6 space-y-4">
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    If you use our mobile application:
                  </p>
                  <ul className="space-y-2 ml-6">
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Device identifiers may be collected</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>App usage data may be analyzed</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>SDK tracking tools may be used</span>
                    </li>
                  </ul>
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    You may manage tracking permissions through your device settings.
                  </p>
                </div>
              )}
            </div>

            {/* Updates to This Cookie Policy */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('policy-updates')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <Icon name="RefreshCw" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Updates to This Cookie Policy</h2>
                </div>
                <Icon 
                  name="ChevronDown" 
                  size={24} 
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['policy-updates'] ? 'rotate-180' : ''}`}
                />
              </button>
              {openSections?.['policy-updates'] && (
                <div className="px-6 pb-6 space-y-4">
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    We may update this Cookie Policy periodically to reflect changes in:
                  </p>
                  <ul className="space-y-2 ml-6">
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Technology</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Legal requirements</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Platform functionality</span>
                    </li>
                  </ul>
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    Updated versions will be posted with a revised effective date.
                  </p>
                </div>
              )}
            </div>

            {/* Contact Information */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('contact-info')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <Icon name="Mail" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Contact Information</h2>
                </div>
                <Icon 
                  name="ChevronDown" 
                  size={24} 
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['contact-info'] ? 'rotate-180' : ''}`}
                />
              </button>
              {openSections?.['contact-info'] && (
                <div className="px-6 pb-6 space-y-4">
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    If you have questions about this Cookie Policy, please contact:
                  </p>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <p className="font-body text-base text-foreground font-semibold mb-2">Draggin Trailers LLC</p>
                    <a 
                      href="mailto:draggintrailers@gmail.com" 
                      className="font-body text-base text-primary hover:underline"
                    >
                      Email: draggintrailers@gmail.com
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CookiePolicy;